import { useState, useEffect, useRef, useCallback } from "react";
import "../../CSS/Inscan.css";
import api from "../../../src/api.js";

import { useRights } from "../RightsContext.jsx"; 

const Inscan_PATH = "/Manifest_Inscan"; 
// ════════════════════════════════════════════
// TOAST COMPONENT
// ════════════════════════════════════════════
function ToastContainer({ toasts }) {
  const icons = { ok: "✓", err: "✕", warn: "⚠", info: "ℹ", extra: "★" };
  return (
    <div className="toast-wrap">
      {toasts.map((t) => (
        <div key={t.id} className={`toast ${t.type}`} style={t.fading ? { opacity: 0, transform: "translateX(40px)" } : {}}>
          <span style={{ fontSize: 15 }}>{icons[t.type] || "ℹ"}</span>
          {t.msg}
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════
export default function Manifest_Inscan() {
  const rights = useRights(Inscan_PATH);
  // State
  const [lookupValue, setLookupValue] = useState("");
  const [lookupError, setLookupError] = useState(false);
  const [currentManifest, setCurrentManifest] = useState(null);
  const [consignmentRows, setConsignmentRows] = useState([]);
  const [extraRows, setExtraRows] = useState([]);
  const [inwardClosed, setInwardClosed] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [scanValue, setScanValue] = useState("");
  const [scanStatus, setScanStatus] = useState({ type: "idle", msg: "Ready to receive" });
  const [flash, setFlash] = useState(null); // { type, cnr, detail, job }
  const [pendingScanCNR, setPendingScanCNR] = useState(null);
  const [showExcRow, setShowExcRow] = useState(false);
  const [excReason, setExcReason] = useState("");
  const [excRemarks, setExcRemarks] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [toasts, setToasts] = useState([]);

  const lookupInputRef = useRef(null);
  const scanInputRef = useRef(null);
  const excReasonRef = useRef(null);

  // Focus lookup on mount
  useEffect(() => {
    lookupInputRef.current?.focus();
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        clearRecvScan();
        cancelException();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // ── TOAST ──
  const toast = useCallback((msg, type = "info") => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, msg, type, fading: false }]);
    setTimeout(() => {
      setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, fading: true } : t)));
      setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 320);
    }, 3000);
  }, []);

  // ── COMPUTED PROGRESS ──
  const progress = (() => {
    const total = consignmentRows.length;
    const received = consignmentRows.filter((r) => r.recvStatus === "received").length;
    const pending = consignmentRows.filter((r) => r.recvStatus === "pending").length;
    const exception = consignmentRows.filter((r) => r.recvStatus === "exception").length;
    const extra = extraRows.length;
    const pct = total ? Math.round((received / total) * 100) : 0;
    return { total, received, pending, exception, extra, pct };
  })();

  const statusPill = (() => {
    const { pct, exception } = progress;
    if (inwardClosed) return { cls: "sp sp-complete", label: "● Closed" };
    if (pct === 100 && !exception) return { cls: "sp sp-complete", label: "● Complete" };
    if (exception) return { cls: "sp sp-exception", label: "● Exception" };
    if (pct > 0) return { cls: "sp sp-partial", label: `● ${pct}% Received` };
    return { cls: "sp sp-open", label: "● In Progress" };
  })();

  const recvRows = consignmentRows.filter((r) => r.recvStatus === "received");
  const totals = {
    total: consignmentRows.length,
    recv: recvRows.length,
    pcs: recvRows.reduce((a, r) => a + r.pcs, 0),
    wt: recvRows.reduce((a, r) => a + r.wt, 0).toFixed(2),
  };

  const allMnfRows = [...consignmentRows, ...extraRows];
  const totPcs = allMnfRows.reduce((a, r) => a + (typeof r.pcs === "number" ? r.pcs : 0), 0);
  const totWt = allMnfRows.reduce((a, r) => a + (typeof r.wt === "number" ? r.wt : 0), 0);

  // ── FILTER + SEARCH ──
  const matchFilter = (r) => {
    if (activeFilter === "all") return true;
    if (activeFilter === "received") return r.recvStatus === "received";
    if (activeFilter === "pending") return r.recvStatus === "pending";
    if (activeFilter === "exception") return r.recvStatus === "exception";
    if (activeFilter === "extra") return r.isExtra || r.recvStatus === "extra";
    return true;
  };

  const matchSearch = (r) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return [r.cnr, r.consignor, r.consignee, r.job].join(" ").toLowerCase().includes(q);
  };

  const filteredRows = [
    ...consignmentRows.filter((r) => matchFilter(r) && matchSearch(r)),
    ...extraRows.filter((r) => matchFilter(r) && matchSearch(r)),
  ];

  // ── LOAD MANIFEST (real API — single dedicated endpoint) ──
  const loadManifest = async () => {
    const raw = lookupValue.trim().toUpperCase();
    if (!raw) { toast("Please enter a manifest number", "warn"); return; }

    setLookupError(false);
    try {
      // Single call: GET /api/manifest/inscan-lookup?manifestNumber=MNF260600008
      // Returns { success, master, details } in one round-trip
      const res = await api.get("/api/manifest/inscan-lookup", { params: { manifestNumber: raw } });
      const { master, details } = res.data;

      // Build currentManifest from master
      setCurrentManifest({
        id:           master.manifest_Id,
        no:           master.manifest_Number,
        date:         master.manifest_Date?.slice(0, 10) ?? "",
        origin:       master.origin_Name  ?? master.origin,
        destination:  master.destination_Name ?? master.destination,
        vehicle:      master.vehical_No  ?? "",
        driver:       master.driver_Name ?? "",
        status:       master.status,
        inscanNumber: master.inscan_Number ?? master.inscanNumber ?? null,
      });

      setInwardClosed(master.status === "C");

      // Map DB columns → component row shape.
      // Inscan_No present → already verified in a previous session → show as received.
      setConsignmentRows(
        (details || []).map((d) => ({
          cnr:        d.consignment_No,
          consignor:  d.consignor ?? "",
          consignee:  d.consignee ?? "",
          pcs:        d.pcs       ?? 0,
          wt:         parseFloat(d.act_Wt ?? 0),
          chgWt:      parseFloat(d.chg_Wt ?? 0),
          dim:        d.dimension ?? "—",
          job:        d.job_Name  ?? (d.job_Id ? `Job #${d.job_Id}` : "—"),
          remark:     d.remark    ?? "",
          inscanNo:   d.inscan_No ?? d.inscanNo ?? null,
          // If Inscan_No is already set in DB → mark received immediately
          // ASP.NET returns either inscanNo (camelCase) or inscan_No depending on serializer config
          recvStatus: (d.inscan_No || d.inscanNo) ? "received" : "pending",
          scanTime:   (d.inscan_Date || d.inscanDate)
                        ? (d.inscan_Date || d.inscanDate).slice(0, 19).replace("T", " ")
                        : "",
          excReason:  "",
          excRemark:  "",
        }))
      );

      setExtraRows([]);
      setActiveFilter("all");
      setSearchQuery("");
      toast(`Manifest ${raw} loaded — ${(details || []).length} consignments`, "ok");
      setTimeout(() => scanInputRef.current?.focus(), 100);
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to load manifest";
      toast(msg, "err");
      setLookupError(true);
      setTimeout(() => setLookupError(false), 1500);
    }
  };

  // ── SCAN HANDLERS ──
  const clearRecvScan = () => {
    setScanValue("");
    setScanStatus({ type: "idle", msg: "Ready to receive" });
    setFlash(null);
    scanInputRef.current?.focus();
  };

  const cancelException = () => {
    setShowExcRow(false);
    setExcReason("");
    setExcRemarks("");
    setPendingScanCNR(null);
    setFlash(null);
    setScanStatus({ type: "idle", msg: "Ready to receive" });
    setScanValue("");
  };

  // ── PRINT ──────────────────────────────────────────────────────────────
  const handlePrint = async () => {
    if (!currentManifest) { toast("Load a manifest first", "warn"); return; }

    // Fetch company details from API
    let companyName    = "";
    let companyAddress = "";
    try {
      const res   = await api.get("/api/manifest/company-detail");
      companyName    = res.data.company?.company_Name    || res.data.company?.Company_Name    || "";
      companyAddress = res.data.company?.company_Address || res.data.company?.Company_Address || "";
    } catch { /* proceed without company info */ }

    // Build table rows HTML
    const allRows = [...consignmentRows, ...extraRows];
    const rowsHtml = allRows.map((r, i) => {
      const statusLabel =
        r.recvStatus === "received"  ? "Received"  :
        r.recvStatus === "exception" ? "Exception" :
        r.recvStatus === "extra"     ? "Extra"     : "Pending";
      return `
        <tr>
          <td>${i + 1}</td>
          <td>${r.cnr ?? ""}</td>
          <td>${r.consignor ?? ""}</td>
          <td>${r.consignee ?? ""}</td>
          <td style="text-align:center">${r.pcs ?? ""}</td>
          <td style="text-align:right">${typeof r.wt === "number" ? r.wt.toFixed(2) : (r.wt ?? "")}</td>
          <td style="text-align:right">${typeof r.chgWt === "number" ? r.chgWt.toFixed(2) : (r.chgWt ?? "")}</td>
          <td>${r.dim ?? "—"}</td>
          <td>${r.job ?? "—"}</td>
          <td>${statusLabel}</td>
          <td>${r.remark ?? ""}${r.excReason ? " | " + r.excReason : ""}</td>
        </tr>`;
    }).join("");

    const totalPcs = allRows.reduce((s, r) => s + (r.pcs || 0), 0);
    const totalWt  = allRows.reduce((s, r) => s + (parseFloat(r.wt) || 0), 0);

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Manifest Inscan — ${currentManifest.no}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; font-size: 11px; color: #111; }

    /* ── Section 1 : Company Header ── */
    .company-header {
      background: #1e2d40;
      color: #fff;
      text-align: center;
      padding: 10px 16px;
    }
    .company-header .co-name {
      font-size: 16px;
      font-weight: 700;
      letter-spacing: .5px;
      text-transform: uppercase;
    }
    .company-header .co-addr {
      font-size: 9px;
      color: #bcc8d8;
      margin-top: 3px;
    }

    /* ── Report Heading ── */
    .report-heading {
      text-align: center;
      padding: 6px 12px;
      font-size: 13px;
      font-weight: 700;
      letter-spacing: .4px;
      text-transform: uppercase;
      border: 1px solid #ccc;
      border-top: none;
      background: #f5f7fa;
      color: #1e2d40;
    }

    /* ── Section 2 : Manifest Info Grid ── */
    .info-section {
      border: 1px solid #ccc;
      border-top: none;
      padding: 8px 12px;
    }
    .info-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 6px 12px;
    }
    .info-cell { display: flex; flex-direction: column; }
    .info-cell .lbl {
      font-size: 8px;
      font-weight: 700;
      color: #666;
      text-transform: uppercase;
      letter-spacing: .4px;
    }
    .info-cell .val {
      font-size: 11px;
      font-weight: 600;
      color: #111;
      margin-top: 1px;
    }

    /* ── Section 3 : Table ── */
    .tbl-section { margin-top: 8px; }
    table { width: 100%; border-collapse: collapse; }
    thead tr { background: #1e2d40; color: #fff; }
    thead th {
      padding: 5px 6px;
      font-size: 9px;
      font-weight: 700;
      text-align: left;
      text-transform: uppercase;
      letter-spacing: .3px;
      border: 1px solid #3a4f66;
    }
    tbody tr:nth-child(even) { background: #f5f7fa; }
    tbody td {
      padding: 4px 6px;
      border: 1px solid #ddd;
      font-size: 10px;
      vertical-align: middle;
    }
    tfoot td {
      padding: 5px 6px;
      font-weight: 700;
      font-size: 10px;
      background: #e8edf3;
      border: 1px solid #ccc;
    }

    /* ── Print Button Bar (hidden when printing) ── */
    .print-bar {
      display: flex;
      justify-content: flex-end;
      padding: 8px 12px;
      background: #f0f2f5;
      border-bottom: 1px solid #ddd;
    }
    .print-bar button {
      background: #1e2d40;
      color: #fff;
      border: none;
      padding: 6px 18px;
      font-size: 13px;
      font-weight: 600;
      border-radius: 4px;
      cursor: pointer;
      letter-spacing: .3px;
    }
    .print-bar button:hover { background: #2e4460; }

    @media print {
      .print-bar { display: none; }
      @page { margin: 10mm; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>

  <!-- Print Button Bar -->
  <div class="print-bar">
    <button onclick="window.print()">🖨️ Print</button>
  </div>

  <!-- Section 1: Company Header -->
  <div class="company-header">
    <div class="co-name">${companyName}</div>
    <div class="co-addr">${companyAddress}</div>
  </div>

  <!-- Section 2: Report Heading -->
  <div class="report-heading">Manifest Inscan Report</div>

  <!-- Section 3: Manifest Info -->
  <div class="info-section">
    <div class="info-grid">
      <div class="info-cell">
        <span class="lbl">Manifest Number</span>
        <span class="val">${currentManifest.no}</span>
      </div>
      <div class="info-cell">
        <span class="lbl">Manifest Date</span>
        <span class="val">${currentManifest.date || "—"}</span>
      </div>
      <div class="info-cell">
        <span class="lbl">Type</span>
        <span class="val">${currentManifest.status === "C" ? "Closed" : currentManifest.status === "O" ? "Open" : "Draft"}</span>
      </div>
      <div class="info-cell">
        <span class="lbl">Driver</span>
        <span class="val">${currentManifest.driver || "—"}</span>
      </div>
      <div class="info-cell">
        <span class="lbl">Driver Mobile</span>
        <span class="val">—</span>
      </div>
      <div class="info-cell">
        <span class="lbl">Vehicle No</span>
        <span class="val">${currentManifest.vehicle || "—"}</span>
      </div>
      <div class="info-cell">
        <span class="lbl">Dispatched From</span>
        <span class="val">${currentManifest.origin || "—"}</span>
      </div>
      <div class="info-cell">
        <span class="lbl">Receiving At</span>
        <span class="val">${currentManifest.destination || "—"}</span>
      </div>
      <div class="info-cell">
        <span class="lbl">Inscan No</span>
        <span class="val">${currentManifest.inscan_Number || currentManifest.inscanNumber || "—"}</span>
      </div>
    </div>
  </div>

  <!-- Section 3: Consignment Table -->
  <div class="tbl-section">
    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>Consignment No</th>
          <th>Consignor</th>
          <th>Consignee</th>
          <th style="text-align:center">Pcs</th>
          <th style="text-align:right">Act Wt (kg)</th>
          <th style="text-align:right">Chg Wt (kg)</th>
          <th>Dimension</th>
          <th>Job</th>
          <th>Status</th>
          <th>Remark / Exception</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHtml}
      </tbody>
      <tfoot>
        <tr>
          <td colspan="4">TOTAL</td>
          <td style="text-align:center">${totalPcs}</td>
          <td style="text-align:right">${totalWt.toFixed(2)}</td>
          <td colspan="5"></td>
        </tr>
      </tfoot>
    </table>
  </div>

</body>
</html>`;

    // Open in a new tab — avoids popup blocker issue with window.open()
    const blob = new Blob([html], { type: "text/html" });
    const url  = URL.createObjectURL(blob);
    window.open(url, "_blank");
    // Revoke after a short delay to free memory
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  // ── VERIFY  —  calls POST /api/manifest/verify-inscan ──────────────────
  // 1. Match CNR against loaded rows (client-side guard).
  // 2. POST to backend → generates/reuses Inscan_Number, saves to DB,
  //    inserts Track_Master with Ref_Type='I'.
  // 3. On success → flip local recvStatus to "received".
  const processReceiveScan = async () => {
    if (inwardClosed) { toast("Inward is closed. No further scans.", "warn"); return; }
    if (!currentManifest) { toast("Load a manifest first", "warn"); return; }
    const raw = scanValue.trim();
    if (!raw) { toast("Enter or scan a consignment number", "warn"); return; }

    const cnrNum = parseInt(raw, 10);
    if (isNaN(cnrNum)) { toast("Consignment number must be numeric", "warn"); return; }

    setScanStatus({ type: "scanning", msg: "Verifying…" });

    const idx = consignmentRows.findIndex((r) => String(r.cnr) === String(cnrNum));

    if (idx >= 0) {
      const row = consignmentRows[idx];

      // Client-side double-scan guard
      if (row.recvStatus === "received") {
        setScanStatus({ type: "dup", msg: "Already received" });
        toast(`${raw} is already marked received`, "warn");
        setFlash({ type: "warn", cnr: raw, detail: `${row.pcs} pcs · ${row.wt} kg · Already Received`, job: "" });
        return;
      }
      if (row.recvStatus === "exception") {
        setScanStatus({ type: "exc", msg: "Already exception" });
        toast(`${raw} was already marked as exception`, "warn");
        return;
      }

      try {
        // POST to backend — generates/reuses Inscan_Number and saves to DB
        // Try to get GPS co-ordinates; proceed even if browser denies
        let latitude  = null;
        let longitude = null;
        try {
          const pos = await new Promise((res, rej) =>
            navigator.geolocation.getCurrentPosition(res, rej, { timeout: 3000 })
          );
          latitude  = pos.coords.latitude;
          longitude = pos.coords.longitude;
        } catch { /* GPS unavailable — stored as null */ }

        const res = await api.post("/api/manifest/verify-inscan", {
          Manifest_Id:    currentManifest.id,
          Consignment_No: cnrNum,
          Latitude:       latitude,
          Longitude:      longitude,
        });

        const { inscanNumber, inscanDate, alreadyDone } = res.data;
        const timeStr = inscanDate
          ? new Date(inscanDate).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
          : new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });

        // Update local row → status = received
        setConsignmentRows((prev) =>
          prev.map((r) =>
            String(r.cnr) === String(cnrNum)
              ? { ...r, recvStatus: "received", scanTime: timeStr, inscanNo: inscanNumber }
              : r
          )
        );

        setScanStatus({ type: "ok", msg: "Received ✓" });
        setFlash({
          type:   "ok",
          cnr:    raw,
          detail: `${row.pcs} pcs · ${row.wt} kg · ${row.consignor} · Inscan: ${inscanNumber}`,
          job:    row.job,
        });

        if (alreadyDone)
          toast(`${raw} was already in-scanned (${inscanNumber})`, "warn");
        else
          toast(`${raw} received — Inscan No: ${inscanNumber}`, "ok");

        setScanValue("");
        scanInputRef.current?.focus();

      } catch (err) {
        const msg = err.response?.data?.message || "Verify failed";
        setScanStatus({ type: "exc", msg: "Error" });
        toast(msg, "err");
      }

    } else {
      // CNR not in manifest → treat as extra
      const isAlreadyExtra = extraRows.find((r) => String(r.cnr) === String(cnrNum));
      if (isAlreadyExtra) { toast(`${raw} already added as extra`, "warn"); return; }
      setPendingScanCNR(raw);
      setScanStatus({ type: "extra", msg: "Not in manifest" });
      setFlash({ type: "extra", cnr: raw, detail: "This consignment was NOT in the manifest", job: "— Extra Item" });
      setShowExcRow(true);
      setTimeout(() => excReasonRef.current?.focus(), 50);
    }
  };

  const markException = () => {
    if (!excReason) { toast("Select an exception reason", "warn"); return; }
    if (pendingScanCNR) {
      const now = new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
      setExtraRows((prev) => [...prev, {
        cnr: pendingScanCNR, consignor: "—", consignee: "—", pcs: "?", wt: 0, dim: "—", ewb: "—", job: "—",
        remark: excRemarks, recvStatus: "exception", scanTime: now,
        excReason, excRemark: excRemarks, isExtra: true,
      }]);
      toast(pendingScanCNR + " marked as Exception — " + excReason, "warn");
    }
    cancelException();
  };

  const markReceivedAnyway = () => {
    if (!pendingScanCNR) return;
    const cnr = pendingScanCNR;
    const now = new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
    setExtraRows((prev) => [...prev, {
      cnr, consignor: "— Extra", consignee: "— Extra", pcs: "?", wt: 0, dim: "—", ewb: "—", job: "Extra",
      remark: "Extra item received", recvStatus: "extra", scanTime: now, excReason: "Extra", excRemark: "", isExtra: true,
    }]);
    toast(cnr + " added as Extra item", "ok");
    cancelException();
  };

  const undoReceive = (cnr) => {
    setConsignmentRows((prev) =>
      prev.map((r) => r.cnr === cnr && r.recvStatus === "received"
        ? { ...r, recvStatus: "pending", scanTime: "", excReason: "", excRemark: "" }
        : r)
    );
    toast(cnr + " receipt undone", "info");
  };

  const confirmCloseInward = () => {
    setInwardClosed(true);
    setShowModal(false);
    toast("Inward receipt for " + currentManifest.no + " closed successfully", "ok");
  };

  const resetAll = () => {
    if (currentManifest && !window.confirm("Reset will clear the current manifest. Continue?")) return;
    setCurrentManifest(null);
    setConsignmentRows([]);
    setExtraRows([]);
    setInwardClosed(false);
    setPendingScanCNR(null);
    setActiveFilter("all");
    setSearchQuery("");
    setLookupValue("");
    setLookupError(false);
    setScanValue("");
    setShowExcRow(false);
    setFlash(null);
    setScanStatus({ type: "idle", msg: "Ready to receive" });
    cancelException();
    toast("Reset complete", "info");
    setTimeout(() => lookupInputRef.current?.focus(), 50);
  };

  // ── SCAN STATUS ICON SVG ──
  const scanStatusIcons = {
    idle: <><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></>,
    scanning: <path d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />,
    ok: <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />,
    err: <><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></>,
    dup: <><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>,
    extra: <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />,
    exc: <><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /></>,
  };

  const scanStatusCls = { idle: "ssb-idle", scanning: "ssb-scanning", ok: "ssb-ok", err: "ssb-err", dup: "ssb-dup", extra: "ssb-extra", exc: "ssb-exc" };

  // ── MODAL DATA ──
  const modalData = currentManifest ? {
    route: `${currentManifest.origin} → ${currentManifest.destination}`,
    exp:   progress.total     + " consignments",
    recv:  progress.received  + " received",
    pend:  progress.pending   + " pending",
    exc:   progress.exception + " exceptions",
    extra: progress.extra     + " extra",
    hasPendingOrExc: progress.pending > 0 || progress.exception > 0,
  } : null;

  return (
    <div className="mis-root">
      {/* ══ HEADER ══ */}

      <div className="page">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <span>Operations</span><span className="bc-sep">›</span>
          <span>Manifest</span><span className="bc-sep">›</span>
          <span className="bc-cur">In Scan (Receive)</span>
        </div>

        {/* Page title + actions */}
        <div className="pg-title-row" style={{ marginTop: 12 }}>
          <div className="pg-title">
            <h1>Manifest In Scan</h1>
            <p>Lookup a manifest, verify each consignment on arrival and close the inward receipt</p>
          </div>
          {currentManifest && (
            <div className="pg-actions">
              <button className="btn btn-outline btn-sm" onClick={resetAll}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 105-8.13M3 3v5h5" /></svg>
                New Lookup
              </button>
              {rights.export && (
                <button className="btn btn-outline btn-sm" onClick={() => toast("Exporting…", "ok")}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                  Excel
                </button>
              )}
              {rights.export && (
                <button className="btn btn-red btn-sm" onClick={handlePrint}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
                Print
              </button>)}
              {/* {!inwardClosed && (
                <button className="btn btn-green btn-lg" onClick={() => setShowModal(true)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  Close Inward
                </button>
              )} */}
            </div>
          )}
        </div>

        {/* ══ MANIFEST LOOKUP ══ */}
        <div className="lookup-card">
          <div className="lookup-band">
            <div className="lb-l">
              <div className="lb-icon">
                <svg viewBox="0 0 24 24" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
              </div>
              <div>
                <div className="lb-title">Manifest Lookup</div>
                <div className="lb-sub">Enter or scan manifest number to load consignment list</div>
              </div>
            </div>
          </div>
          <div className="lookup-body">
            <div className="lookup-row">
              <div className="lookup-field">
                <label>Manifest Number</label>
                <div className={`lookup-input-wrap${lookupError ? " error" : ""}`}>
                  <span className="li-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 5v4M3 5h4M3 19v-4M3 19h4M21 5v4M21 5h-4M21 19v-4M21 19h-4M8 5v14M12 5v14M16 5v14" />
                    </svg>
                  </span>
                  <input
                    ref={lookupInputRef}
                    type="text"
                    className="lookup-input"
                    value={lookupValue}
                    onChange={(e) => setLookupValue(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && loadManifest()}
                    placeholder="e.g. MNF260600008"
                    autoComplete="off"
                    spellCheck="false"
                  />
                </div>
                <div className="scan-kbd">
                  Press <span className="kbd">Enter</span> to load
                </div>
              </div>

              <button className="btn btn-primary" onClick={loadManifest} style={{ alignSelf: "flex-end", padding: "11px 22px" }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="15" height="15"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
                Load Manifest
              </button>
            </div>
          </div>
        </div>

        {/* ══ MANIFEST SUMMARY ══ */}
        {currentManifest && (
          <>
            {/* Manifest detail card */}
            <div className="mnf-detail-card">
              <div className="mnf-hdr-band">
                <div className="mnf-hdr-l">
                  <div className="mnf-hdr-icon">
                    <svg viewBox="0 0 24 24" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                  </div>
                  <div>
                    <div className="mnf-hdr-no">{currentManifest.no}</div>
                    <div className="mnf-hdr-sub">Out Scan: {currentManifest.date}</div>
                  </div>
                </div>
                <div className="mnf-hdr-r">
                  <span className={statusPill.cls}>{statusPill.label}</span>
                </div>
              </div>
              <div className="mnf-body">
                {/* Route strip */}
                <div className="route-strip">
                  <div className="rs-place">
                    <div className="rs-lbl">Dispatched From</div>
                    <div className="rs-name">{currentManifest.origin}</div>
                  </div>
                  <div className="rs-arrow">
                    <div className="rs-line"></div>
                    <span className="rs-truck">📦</span>
                    <div className="rs-line"></div>
                  </div>
                  <div className="rs-place" style={{ textAlign: "right" }}>
                    <div className="rs-lbl">Receiving At</div>
                    <div className="rs-name">{currentManifest.destination}</div>
                  </div>
                </div>

                {/* Meta info */}
                <div className="mnf-meta-grid">
                  <div className="meta-cell"><div className="meta-lbl">Manifest Date</div><div className="meta-val">{currentManifest.date}</div></div>
                  <div className="meta-cell"><div className="meta-lbl">Driver</div><div className="meta-val">{currentManifest.driver || "—"}</div></div>
                  <div className="meta-cell"><div className="meta-lbl">Vehicle / LR No.</div><div className="meta-val navy">{currentManifest.vehicle || "—"}</div></div>
                  <div className="meta-cell"><div className="meta-lbl">Status</div><div className="meta-val teal">{currentManifest.status === "C" ? "Closed" : currentManifest.status === "O" ? "Open" : "Draft"}</div></div>
                </div>

                {/* Progress counters */}
                <span className="sec-lbl">Receipt Progress</span>
                <div className="progress-row">
                  <div className="prog-box total"><div className="pb-val">{progress.total}</div><div className="pb-lbl">Expected</div></div>
                  <div className="prog-box received"><div className="pb-val">{progress.received}</div><div className="pb-lbl">Received ✓</div></div>
                  <div className="prog-box pending"><div className="pb-val">{progress.pending}</div><div className="pb-lbl">Pending ⏳</div></div>
                  <div className="prog-box exception"><div className="pb-val">{progress.exception}</div><div className="pb-lbl">Exception ⚠</div></div>
                  <div className="prog-box extra"><div className="pb-val">{progress.extra}</div><div className="pb-lbl">Extra ★</div></div>
                </div>
                <div className="prog-bar-wrap">
                  <div className="prog-bar-fill" style={{ width: progress.pct + "%" }}></div>
                </div>
                <div className="prog-pct-txt">{progress.pct}% received</div>
              </div>
            </div>

            {/* ══ SCAN PANEL ══ */}
            <div className="scan-panel">
              <div className="scan-band">
                <div>
                  <div className="scan-band-title">📡 Receive Scan</div>
                  <div className="scan-band-sub">Scan each consignment to mark as received</div>
                </div>
                <span className="scan-counter">{progress.received} / {progress.total} Verified</span>
              </div>
              <div className="scan-body">
                <div className="scan-input-row">
                  <div className="scan-field">
                    <label>Scan Manifest Number</label>
                    <div className="scan-input-wrap">
                      <span className="scan-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 5v4M3 5h4M3 19v-4M3 19h4M21 5v4M21 5h-4M21 19v-4M21 19h-4M8 5v14M12 5v14M16 5v14" />
                        </svg>
                      </span>
                      <input
                        ref={scanInputRef}
                        type="text"
                        className="scan-input"
                        value={scanValue}
                        onChange={(e) => setScanValue(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && processReceiveScan()}
                        placeholder="Scan or type CNR…"
                        autoComplete="off"
                        spellCheck="false"
                        disabled={inwardClosed}
                        style={inwardClosed ? { opacity: 0.5 } : {}}
                      />
                      {scanValue && (
                        <button className="scan-clear" onClick={clearRecvScan}>×</button>
                      )}
                    </div>
                    <div className="scan-kbd">
                      Press <span className="kbd">Enter</span> to verify &nbsp;|&nbsp;
                      <span className="kbd">Esc</span> to clear
                    </div>
                  </div>
                  <div className={`scan-status-badge ${scanStatusCls[scanStatus.type] || "ssb-idle"}`}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                      {scanStatusIcons[scanStatus.type]}
                    </svg>
                    <span>{scanStatus.msg}</span>
                  </div>
                  {rights.add && (<button className="btn btn-primary" onClick={processReceiveScan} style={{ alignSelf: "flex-end", padding: "11px 20px" }} disabled={inwardClosed}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="15" height="15"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    Verify
                  </button>)}
                </div>

                {/* Exception reason row */}
                {showExcRow && (
                  <div className="exc-reason-row">
                    <div className="field" style={{ flex: 1 }}>
                      <label>Exception Reason <span style={{ color: "var(--red)" }}>*</span></label>
                      <select ref={excReasonRef} value={excReason} onChange={(e) => setExcReason(e.target.value)}>
                        <option value="">— Select reason —</option>
                        <option value="Short Delivery">Short Delivery</option>
                        <option value="Damaged Outer">Damaged Outer Packaging</option>
                        <option value="Wet / Stained">Wet / Stained Package</option>
                        <option value="Wrong Destination">Wrong Destination</option>
                        <option value="Pieces Short">Pieces Short</option>
                        <option value="Weight Mismatch">Weight Mismatch</option>
                        <option value="Pilferage">Pilferage Suspected</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="field" style={{ flex: 1 }}>
                      <label>Remarks</label>
                      <input type="text" value={excRemarks} onChange={(e) => setExcRemarks(e.target.value)} placeholder="Additional details…" />
                    </div>
                    <button className="btn btn-red" onClick={markException} style={{ alignSelf: "flex-end" }}>⚠ Mark Exception</button>
                    <button className="btn btn-green" onClick={markReceivedAnyway} style={{ alignSelf: "flex-end" }}>✓ Receive Anyway</button>
                    <button className="btn btn-outline" onClick={cancelException} style={{ alignSelf: "flex-end" }}>Cancel</button>
                  </div>
                )}

                {/* Flash Preview */}
                {flash && (
                  <div className={`scan-flash ${flash.type === "warn" ? "ok" : flash.type}`}>
                    <div className="sf-l">
                      <span className="sf-icon">{flash.type === "ok" || flash.type === "warn" ? "✅" : flash.type === "extra" ? "⚠️" : "📦"}</span>
                      <div>
                        <div className="sf-cnr">{flash.cnr}</div>
                        <div className="sf-detail">{flash.detail}</div>
                      </div>
                    </div>
                    <div className="sf-r">
                      {flash.job && <span className="job-tag">{flash.job}</span>}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ══ CONSIGNMENT LIST TABLE ══ */}
            <div className="manifest-tbl-card">
              <div className="tbl-hdr-band">
                <div className="tbl-hdr-title">📋 Manifest Consignments</div>
                <span className="tbl-count-badge">{filteredRows.length} entries</span>
              </div>
              <div className="tbl-filter-row">
                {[
                  { key: "all", label: "All", extra: "" },
                  { key: "received", label: "✓ Received", extra: "f-received" },
                  { key: "pending", label: "⏳ Pending", extra: "f-pending" },
                  { key: "exception", label: "⚠ Exception", extra: "f-exception" },
                  { key: "extra", label: "★ Extra", extra: "f-extra" },
                ].map(({ key, label, extra }) => (
                  <button
                    key={key}
                    className={`filter-chip ${extra} ${activeFilter === key ? "active" : ""}`}
                    onClick={() => setActiveFilter(key)}
                  >
                    {label}
                  </button>
                ))}
                <div className="tbl-search">
                  <input type="text" placeholder="🔍 Search CNR, consignor…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                </div>
              </div>
              <div className="tbl-wrap">
                <table className="mnf-in-table">
                  <thead>
                    <tr>
                      <th className="center" style={{ width: 38 }}>#</th>
                      <th>Consignment No.</th>
                      <th>Consignor</th>
                      <th>Consignee</th>
                      <th className="center">Pcs</th>
                      <th className="right">Wt (kg)</th>
                      <th className="center">Dimension</th>
                      <th>Job</th>
                      <th className="center">Status</th>
                      <th>Scan Time</th>
                      <th>Remark / Exception</th>
                      <th className="center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.length === 0 ? (
                      <tr>
                        <td colSpan="12">
                          <div className="empty-state">
                            <div className="empty-icon">🔍</div>
                            <div className="empty-title">No matching records</div>
                            <div className="empty-sub">Try changing the filter or search query</div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredRows.map((r, seq) => {
                        const rowCls =
                          r.recvStatus === "received" ? "row-received" :
                          r.recvStatus === "exception" ? "row-exception" :
                          r.isExtra && r.recvStatus === "extra" ? "row-extra" : "";
                        const seqCls =
                          { received: "seq-received", pending: "seq-pending", exception: "seq-exception", extra: "seq-extra" }[r.recvStatus] || "seq-pending";
                        const pill =
                          r.recvStatus === "received" ? <span className="pill pill-received">✓ Received</span> :
                          r.recvStatus === "exception" ? <span className="pill pill-exception">⚠ Exception</span> :
                          r.recvStatus === "extra" ? <span className="pill pill-extra">★ Extra</span> :
                          <span className="pill pill-pending">⏳ Pending</span>;
                        return (
                          <tr key={r.cnr} className={rowCls}>
                            <td className="center"><span className={`scan-seq ${seqCls}`}>{seq + 1}</span></td>
                            <td>
                              <span className="sn-col">{r.cnr}</span>
                              {r.isExtra && <span className="job-tag" style={{ marginLeft: 5, background: "var(--purple-lt)", color: "var(--purple)" }}>EXTRA</span>}
                            </td>
                            <td style={{ fontSize: 12.5 }}>{r.consignor}</td>
                            <td style={{ fontSize: 12.5 }}>{r.consignee}</td>
                            <td className="center"><strong style={{ color: "var(--blue)", fontSize: 14 }}>{r.pcs}</strong></td>
                            <td className="right"><strong>{typeof r.wt === "number" ? r.wt.toFixed(2) : r.wt}</strong></td>
                            <td className="center"><span className="dim-tag">{r.dim}</span></td>
                            <td><span className="job-tag">{r.job}</span></td>
                            <td className="center">{pill}</td>
                            <td><span className="scan-time">{r.scanTime || "—"}</span></td>
                            <td style={{ fontSize: 12, color: "var(--slate)" }}>
                              {r.remark || ""}
                              {r.excReason && <div className="exc-note">⚠ {r.excReason}{r.excRemark ? " — " + r.excRemark : ""}</div>}
                            </td>
                            <td className="center">
                              {r.recvStatus === "received" && (
                                <button
                                  className="tbl-act-btn"
                                  onClick={() => undoReceive(r.cnr)}
                                  title="Undo receive"
                                  style={{ background: "var(--amber-lt)", borderColor: "rgba(217,119,6,.25)", color: "var(--amber)" }}
                                >↩</button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  {filteredRows.length > 0 && (
                    <tfoot>
                      <tr className="row-subtotal">
                        <td colSpan="4" style={{ padding: "10px 12px" }}>SUB TOTAL</td>
                        <td className="center" style={{ fontSize: 14 }}>{totPcs}</td>
                        <td className="right" style={{ fontSize: 14 }}>{totWt.toFixed(2)} kg</td>
                        <td colSpan="6"></td>
                      </tr>
                      <tr className="row-grand">
                        <td colSpan="4" style={{ padding: "10px 12px" }}>
                          GRAND TOTAL — <span style={{ color: "rgba(255,255,255,.7)", fontSize: 12, fontWeight: 600 }}>
                            {consignmentRows.length} manifest + {extraRows.length} extra
                          </span>
                        </td>
                        <td className="center" style={{ fontSize: 15 }}>{totPcs}</td>
                        <td className="right" style={{ fontSize: 15 }}>{totWt.toFixed(2)} kg</td>
                        <td colSpan="6"></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>

            {/* Totals strip */}
            <div className="totals-strip">
              <div className="tot-box blue">
                <div className="tot-lbl">Total Expected</div>
                <div className="tot-val">{totals.total}</div>
                <div className="tot-unit">consignments</div>
              </div>
              <div className="tot-box green">
                <div className="tot-lbl">Received</div>
                <div className="tot-val">{totals.recv}</div>
                <div className="tot-unit">consignments</div>
              </div>
              <div className="tot-box teal">
                <div className="tot-lbl">Total Pieces</div>
                <div className="tot-val">{totals.pcs}</div>
                <div className="tot-unit">packages</div>
              </div>
              <div className="tot-box navy">
                <div className="tot-lbl">Total Weight</div>
                <div className="tot-val">{totals.wt}</div>
                <div className="tot-unit">kg received</div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ══ CLOSE INWARD MODAL ══ */}
      {showModal && modalData && (
        <div className="overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-hd">
              <h3>Close Inward Receipt</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: "var(--slate)", marginBottom: 14, lineHeight: 1.7 }}>
                Confirm closing the inward receipt for manifest{" "}
                <strong style={{ color: "var(--navy)" }}>{currentManifest.no}</strong>.
                Once closed, no further scans will be accepted.
              </p>
              <div className="modal-details-box">
                <div className="modal-detail-row"><span>Manifest No.</span><span>{currentManifest.no}</span></div>
                <div className="modal-detail-row"><span>Route</span><span>{modalData.route}</span></div>
                <div className="modal-detail-row"><span>Expected</span><span>{modalData.exp}</span></div>
                <div className="modal-detail-row"><span>Received ✓</span><span style={{ color: "var(--green)" }}>{modalData.recv}</span></div>
                <div className="modal-detail-row"><span>Pending ⏳</span><span style={{ color: "var(--amber)" }}>{modalData.pend}</span></div>
                <div className="modal-detail-row"><span>Exception ⚠</span><span style={{ color: "var(--red)" }}>{modalData.exc}</span></div>
                <div className="modal-detail-row"><span>Extra ★</span><span style={{ color: "var(--purple)" }}>{modalData.extra}</span></div>
              </div>
              {modalData.hasPendingOrExc && (
                <div className="modal-warning">
                  ⚠ There are pending / exception consignments. Are you sure you want to close?
                </div>
              )}
            </div>
            <div className="modal-ft">
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-green" onClick={confirmCloseInward}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Confirm Close Inward
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ TOASTS ══ */}
      <ToastContainer toasts={toasts} />
    </div>
  );
}